import { Restaurant } from "@/types/places";
import { BLUR_URL, getUnifiedRestaurantsImages } from "@/utils/images";
import Link from "next/link";
import { useLanguage } from "@/hooks/useLanguage";
import Image from "next/image";
import { BlueRibbonLogos, MichelinLogos, PriceRangeBadge } from "./badges";
import { shortenAddress } from "@/utils/util";
import { STATIC_ENDPOINT } from "@/config/endpoint/endpoint";

export default function RestaurantCard({ restaurant }: { restaurant: Restaurant }) {
  const { language } = useLanguage();

  return (
    <Link href={`/${language}/places/restaurants/${restaurant.id}`}>
      <div className="group cursor-pointer">
        <div className="mb-4 overflow-hidden rounded-xl relative h-48 sm:h-52 md:h-48 lg:h-48">
          <Image 
            src={getUnifiedRestaurantsImages(restaurant.id, restaurant.images.officialImages, restaurant.images.userImages)[0]} 
            alt={`${restaurant.name[language]}-image`}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            placeholder="blur"
            blurDataURL={BLUR_URL}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-gray-900 group-hover:text-gray-600 transition-colors break-words">
            {restaurant.name[language]}
          </h3>
          <div className='flex items-center gap-1'>
            {restaurant.michelin_info && (
              <MichelinLogos count={restaurant.michelin_info.michelinAward} />
            )}
            {restaurant.blueribbon_info && (
              <BlueRibbonLogos count={restaurant.blueribbon_info.ribbonType} />
            )}
          </div>
        </div>
        
        <div className="flex flex-col flex-wrap text-sm text-gray-600 mt-1">
          <div>
            {shortenAddress(restaurant.address?.[language], language)}
          </div>
          <div className="flex flex-wrap items-center text-sm text-gray-600 mt-1.5">
            <span key={restaurant.food_category[language]}>
              {restaurant.food_category[language]}
            </span>
            {restaurant.michelin_info && (
              <>
                &nbsp;•&nbsp;
                <PriceRangeBadge priceRange={restaurant.michelin_info.priceSlug} />
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
} 